import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { type RouterNetworkChangedData } from '@okvc/schema';

import useWebSocketMessage from './useWebSocketMessage.js';
import { RoutersApiKeys } from '../../api/routers.api.js';
import { CARRIER_LABELS } from '../../components/Router/routerSimForm.js';
import { type Patient, WebSocketMessageTypes } from '../../models/index.js';
import { addSnackPack } from '../../redux/reducers/common.js';
import { routerNetworkChanged } from '../../redux/reducers/routerNetwork.js';

const UNKNOWN_PATIENT_LABEL = 'A monitored patient';

export interface NetworkChangeNotice {
  message: string;
  isProblem: boolean;
}

export function describeNetworkChange(change: RouterNetworkChangedData, patientLabel: string): NetworkChangeNotice {
  const toCarrier = change.toCarrier ? CARRIER_LABELS[change.toCarrier] : 'the other SIM';
  switch (change.type) {
    case 'sim_switched': {
      const fromCarrier = change.fromCarrier ? ` from ${CARRIER_LABELS[change.fromCarrier]}` : '';
      return { message: `${patientLabel}: network switched${fromCarrier} to ${toCarrier}`, isProblem: false };
    }
    case 'link_up':
      return { message: `${patientLabel}: router back online on ${toCarrier}`, isProblem: false };
    case 'link_down':
      return { message: `${patientLabel}: router offline`, isProblem: true };
    case 'flapping':
      return { message: `${patientLabel}: network unstable, now on ${toCarrier}`, isProblem: true };
    case 'capacity_test_started':
      return { message: `${patientLabel}: network test started, video paused for a few minutes`, isProblem: false };
    case 'capacity_test_finished':
      return { message: `${patientLabel}: network test finished, video resuming`, isProblem: false };
    case 'switch_requested':
      return { message: `${patientLabel}: switching network to ${toCarrier}, video may pause`, isProblem: false };
    case 'switch_failed':
      return { message: `${patientLabel}: network switch to ${toCarrier} failed`, isProblem: true };
    case 'primary_changed':
      return { message: `${patientLabel}: ${toCarrier} is now the primary SIM`, isProblem: false };
  }
}

export function useRouterNetworkSync(patients: Patient[]): void {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const change = useWebSocketMessage<RouterNetworkChangedData>(WebSocketMessageTypes.ROUTER_NETWORK_CHANGED);
  const patientsRef = useRef(patients);
  patientsRef.current = patients;

  useEffect(() => {
    if (!change?.data) {
      return;
    }
    const patient = patientsRef.current.find(({ id }) => id === change.data.patientId);
    const patientLabel = patient ? `${patient.firstName} ${patient.lastName}` : UNKNOWN_PATIENT_LABEL;
    const notice = describeNetworkChange(change.data, patientLabel);

    dispatch(routerNetworkChanged(change.data));
    dispatch(addSnackPack({ message: notice.message, error: notice.isProblem }));
    void queryClient.invalidateQueries([RoutersApiKeys.Routers]);
  }, [change, dispatch, queryClient]);
}
